<script lang="ts">
  import { onDestroy } from 'svelte';
  import { liveTicketDetailStore } from '../../lib/convexRealtime';
  import type { ValidationResponse, InventoryItem } from '../../lib/types';
  import { myTickets } from '../session';
  import { gotoCommunity, gotoHome, gotoEvent, markSuccessSeen } from '../router';
  import { accentStyle, formatEventDate, formatPrice } from '../format';
  import CheckoutPanel from '../components/CheckoutPanel.svelte';
  import InvitePanel from '../components/InvitePanel.svelte';
  import TierLadder from '../components/TierLadder.svelte';
  import MarkdownView from '../ui/MarkdownView.svelte';
  import Notice from '../ui/Notice.svelte';
  import ErrorPanel from '../ui/ErrorPanel.svelte';
  import LoadingBlock from '../ui/LoadingBlock.svelte';

  export let eventId: string;
  export let code: string;
  export let showSuccess = false;

  let detailStore: ReturnType<typeof liveTicketDetailStore> | null = null;
  let unsubscribe: (() => void) | null = null;
  let subscribedKey: string | null = null;

  let detail: ValidationResponse | null = null;
  let loading = true;
  let error: string | null = null;

  let showCheckout = false;
  let successBanner = false;

  $: key = `${eventId}::${code}`;
  $: if (eventId && code && key !== subscribedKey) {
    subscribedKey = key;
    showCheckout = false;
    unsubscribe?.();
    detailStore?.dispose();
    detailStore = liveTicketDetailStore(eventId, code);
    unsubscribe = detailStore.subscribe(state => {
      detail = state.data ?? null;
      loading = state.loading;
      error = state.error;
    });
  }

  // Stripe success redirect: show confirmation once, then clean the URL so
  // refreshes don't re-trigger it. Redemption state itself arrives live.
  $: if (showSuccess) {
    successBanner = true;
    showCheckout = false;
    markSuccessSeen();
  }

  onDestroy(() => {
    unsubscribe?.();
    detailStore?.dispose();
  });

  $: user = detail?.user ?? null;
  $: event = detail?.event ?? null;
  $: inventory = detail?.inventory ?? [];
  $: ticketPrice = detail?.ticketPrice ?? 0;
  $: redeemed = user?.redeemed ?? false;
  $: currency = event?.ticket?.currency ?? 'eur';
  $: ticketCommunity = detail?.community ?? null;

  // Honest issued-row states. An issued row minted by the buy seam (origin
  // 'purchase', or legacy 'self') is a checkout anchor, not a ticket:
  //   - with payment just returned (?success in flight): confirming, webhook
  //     pending — the live query flips the row to redeemed on its own;
  //   - without success: the checkout was abandoned — render no ticket at all.
  // A manager-created issued row (origin 'manual'/'api', pay-at-door) is a
  // legitimate reservation and keeps today's "Your reservation" rendering.
  const anchorOrigins = new Set(['purchase', 'self']);
  $: isAnchor = user?.status === 'issued' && anchorOrigins.has(user?.origin ?? '');
  // successBanner survives markSuccessSeen()'s URL cleanup, so the confirming
  // state holds until the webhook redeems the row (or the user leaves).
  $: confirmingPayment = isAnchor && !redeemed && (showSuccess || successBanner);
  $: abandonedCheckout = isAnchor && !redeemed && !confirmingPayment;

  // The Convex ticket _id (needed for invites/question) comes from the
  // signed-in user's own ticket feed — invites stay owner-only.
  $: ownTicket = $myTickets.tickets.find(ticket => ticket.code === code && ticket.eventId === eventId) ?? null;
  $: ticketId = ownTicket?.id ?? null;

  // 'purchase' = members who bought their own ticket (legacy 'self'); they are
  // the guest list and may bring plus-ones (mirrors the server-side allowlist).
  const inviteEligibleOrigins = new Set(['direct', 'admin', 'import', 'personal', 'purchase', 'self']);
  $: canManageInvites =
    Boolean(event?.ticket?.allowInvites) &&
    Boolean(ticketId) &&
    inviteEligibleOrigins.has(user?.origin ?? 'direct');

  $: purchasedBySku = (() => {
    const map = new Map<string, number>();
    for (const extra of user?.extras ?? []) {
      map.set(extra.sku, (map.get(extra.sku) ?? 0) + (extra.quantity ?? 0));
    }
    return map;
  })();

  $: availableExtras = inventory.filter((item: InventoryItem) => {
    const remaining = Math.max(0, item.maxPerOrder - (purchasedBySku.get(item.sku) ?? 0));
    return item.maxPerOrder > 0 && remaining > 0 && (item.isUnlimited || item.available > 0);
  });

  $: preCheckoutQuestion = (() => {
    const value = event?.ticket?.preCheckoutQuestion ?? null;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  })();

  function friendlyError(message: string): string {
    if (/authentication required/i.test(message)) {
      return 'This ticket belongs to a different account. Sign in with the account it was issued to.';
    }
    // Strip Convex server-error boilerplate down to the actual message.
    return message.replace(/\[CONVEX[^\]]*\]\s*(\[Request ID:[^\]]*\]\s*)?Server Error\s*(Uncaught Error:)?\s*/i, '').trim() || message;
  }

  function extraName(sku: string): string {
    return inventory.find(item => item.sku === sku)?.name ?? event?.extras?.find(extra => extra.sku === sku)?.name ?? sku;
  }

  // --- calendar (.ics download) ----------------------------------------------
  let calendarStatus: 'idle' | 'success' | 'error' = 'idle';
  let calendarTimer: ReturnType<typeof setTimeout> | null = null;

  function downloadCalendarFile() {
    if (!event?.schedule?.start || !event?.schedule?.end) return;
    try {
      const formatForICS = (iso: string) => {
        const date = new Date(iso);
        if (Number.isNaN(date.getTime())) return '';
        return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
      };
      const ticketUrl = `${window.location.origin}?event=${encodeURIComponent(eventId)}&id=${encodeURIComponent(code)}`;
      const icsData = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'BEGIN:VEVENT',
        `SUMMARY:${event.headline ?? event.name}`,
        `DTSTART:${formatForICS(event.schedule.start)}`,
        `DTEND:${formatForICS(event.schedule.end)}`,
        `LOCATION:${event.location?.name ?? ''}`,
        `DESCRIPTION:🎫 Your ticket: ${ticketUrl}`,
        'END:VEVENT',
        'END:VCALENDAR'
      ].join('\r\n');
      const blob = new Blob([icsData], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${event.id ?? 'event'}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      calendarStatus = 'success';
    } catch (err) {
      console.error('Failed to generate calendar file', err);
      calendarStatus = 'error';
    }
    if (calendarTimer) clearTimeout(calendarTimer);
    calendarTimer = setTimeout(() => (calendarStatus = 'idle'), 2600);
  }

  onDestroy(() => {
    if (calendarTimer) clearTimeout(calendarTimer);
  });

  $: whatsappGroupUrl = (() => {
    const value = event?.whatsappGroupUrl;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  })();
</script>

<div class="ticket-page container themed" style={accentStyle(ticketCommunity?.themeColor)}>
  <button type="button" class="ticket-page__back" on:click={() => gotoHome({ push: false })}>
    <span aria-hidden="true">←</span> Back to events
  </button>

  {#if loading && !detail}
    <LoadingBlock label="Loading your ticket…" />
  {:else if error}
    <ErrorPanel
      title="This ticket link didn't work"
      hint={friendlyError(error)}
      detail={error}
    />
  {:else if !detail || !user || !event}
    <ErrorPanel
      title="This ticket link doesn't match anything"
      hint="Double-check the link you were sent, or contact the organizers."
      detail={`No ticket found for event=${eventId} code=${code}`}
    />
  {:else if abandonedCheckout}
    <!-- Abandoned checkout: an anchor row with no payment. Render no ticket —
         just an honest notice and a single way back to the event page. -->
    <section class="ticket-page__abandoned surface surface--sunken" aria-label="Checkout not completed">
      <h1>This checkout wasn't completed</h1>
      <p>No payment was taken. You can start again from the event page whenever you're ready.</p>
      <button type="button" class="accent" on:click={() => gotoEvent(eventId, { push: true })}>
        Back to the event
      </button>
    </section>
  {:else}
    {#if confirmingPayment}
      <!-- Payment just returned; the webhook is redeeming the row. The live
           query flips this page to the real ticket on its own within seconds. -->
      <Notice variant="success">
        Confirming your payment… your ticket appears here automatically in a few seconds.
      </Notice>
    {:else if successBanner}
      <Notice variant="success">
        {#if redeemed}
          Payment confirmed — you're in. See you there!
        {:else}
          Payment received. Your ticket is being finalized — this updates automatically in a few seconds.
        {/if}
      </Notice>
    {/if}

    {#if ticketCommunity && !ticketCommunity.viewerIsActiveMember}
      {@const joinCommunityRef = ticketCommunity}
      <div class="ticket-page__join surface surface--sunken">
        <div>
          <p class="ticket-page__join-title">This event is hosted by {joinCommunityRef.name}</p>
          <p class="ticket-page__join-hint">
            {joinCommunityRef.visibility === 'public'
              ? 'Join the community to hear about their next events.'
              : 'Apply to join the community to hear about their next events.'}
          </p>
        </div>
        <button type="button" class="secondary" on:click={() => gotoCommunity(joinCommunityRef.slug, { push: true })}>
          {joinCommunityRef.visibility === 'public' ? 'Join' : 'View community'}
        </button>
      </div>
    {/if}

    {#if event.flyerUrl}
      <img class="ticket-page__flyer" src={event.flyerUrl} alt="" />
    {/if}

    <header class="ticket-page__event surface surface--raised">
      <div class="ticket-page__event-info">
        <p class="ticket-page__eyebrow">
          {redeemed ? 'Your ticket' : confirmingPayment ? 'Your ticket' : 'Your reservation'}
        </p>
        <h1>{event.name}</h1>
        {#if event.headline && event.headline !== event.name}
          <p class="ticket-page__headline">{event.headline}</p>
        {/if}
        <dl class="ticket-page__facts">
          {#if formatEventDate(event.schedule)}
            <div>
              <dt>When</dt>
              <dd>{formatEventDate(event.schedule)}</dd>
            </div>
          {/if}
          {#if event.location?.name}
            <div>
              <dt>Where</dt>
              <dd>
                {event.location.name}
                {#if event.location.mapUrl}
                  · <a href={event.location.mapUrl} target="_blank" rel="noopener noreferrer">Map</a>
                {/if}
              </dd>
            </div>
          {/if}
          <div>
            <dt>Guest</dt>
            <dd>{user.person.name}</dd>
          </div>
        </dl>
      </div>
      <div class="ticket-page__status" class:ticket-page__status--confirmed={redeemed}>
        {redeemed ? 'Confirmed' : confirmingPayment ? 'Confirming…' : 'Not paid yet'}
      </div>
    </header>

    {#if event.description}
      <section class="ticket-page__about surface surface--sunken" aria-label="About this event">
        <MarkdownView source={event.description} />
      </section>
    {/if}

    {#if confirmingPayment}
      <section class="ticket-page__cta surface surface--sunken">
        <p>We're confirming your payment with Stripe. This page updates on its own — no need to pay again.</p>
      </section>
    {:else if showCheckout}
      <CheckoutPanel
        price={redeemed ? 0 : ticketPrice}
        ticketCode={code}
        {event}
        {inventory}
        tiers={detail?.tiers ?? []}
        extrasOnly={redeemed}
        purchasedExtras={user.extras ?? []}
        {ticketId}
        question={preCheckoutQuestion}
        initialAnswer={user.preCheckoutAnswer ?? ''}
        on:back={() => (showCheckout = false)}
      />
    {:else if !redeemed}
      {#if detail?.eventCapacity?.soldOut}
        <section class="ticket-page__cta surface surface--sunken">
          <p>This event has sold out — your reservation can no longer be completed.</p>
        </section>
      {:else}
        <section class="ticket-page__cta surface surface--sunken">
          <p>
            {#if (detail?.tiers ?? []).length > 0}
              Complete your ticket to lock in your spot.
            {:else}
              Complete your ticket for <strong>{formatPrice(ticketPrice, currency)}</strong> to lock in your spot.
            {/if}
          </p>
          <button type="button" class="accent" on:click={() => (showCheckout = true)}>Complete your ticket</button>
          {#if (detail?.tiers ?? []).length > 0}
            <div class="ticket-page__ladder">
              <TierLadder tiers={detail?.tiers ?? []} {currency} />
            </div>
          {/if}
        </section>
      {/if}
    {:else}
      <section class="ticket-page__stub" aria-label="Ticket code">
        <div class="ticket-page__stub-rail" aria-hidden="true">Admit one</div>
        <div class="ticket-page__stub-body">
          <p class="ticket-page__stub-eyebrow">Entry code</p>
          <p class="ticket-page__code-value">{code}</p>
          <p class="ticket-page__code-hint">Show this at the door.</p>
        </div>
      </section>

      {#if (user.extras ?? []).length > 0}
        <section class="ticket-page__extras surface surface--sunken" aria-labelledby="extras-heading">
          <h3 id="extras-heading">Your extras</h3>
          <ul>
            {#each user.extras ?? [] as extra (extra.sku + String(extra.purchasedAt ?? ''))}
              <li>
                <span>{extraName(extra.sku)} × {extra.quantity}</span>
                {#if extra.totalPrice != null}
                  <span>{formatPrice(extra.totalPrice, extra.currency ?? currency)}</span>
                {/if}
              </li>
            {/each}
          </ul>
        </section>
      {/if}

      <section class="ticket-page__actions">
        {#if event.schedule?.start && event.schedule?.end}
          <button type="button" class="secondary" on:click={downloadCalendarFile}>
            {calendarStatus === 'success'
              ? 'Calendar file downloaded ✓'
              : calendarStatus === 'error'
              ? 'Calendar failed — try again'
              : 'Add to calendar'}
          </button>
        {/if}
        {#if whatsappGroupUrl}
          <a class="ticket-page__whatsapp" href={whatsappGroupUrl} target="_blank" rel="noopener noreferrer">
            Join the WhatsApp group
          </a>
        {/if}
        {#if availableExtras.length > 0}
          <button type="button" class="secondary" on:click={() => (showCheckout = true)}>Add extras</button>
        {/if}
      </section>

      {#if canManageInvites && ticketId}
        <InvitePanel {ticketId} />
      {/if}
    {/if}
  {/if}
</div>

<style>
  .ticket-page {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    padding: var(--space-6) var(--space-4) var(--space-8);
    max-width: var(--content-max-width);
  }

  .ticket-page__join {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-4) var(--space-5);
    border-radius: var(--radius-lg);
    flex-wrap: wrap;
  }

  .ticket-page__join-title {
    margin: 0;
    font-weight: 600;
    font-size: var(--font-size-sm);
  }

  .ticket-page__join-hint {
    margin: var(--space-1) 0 0;
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .ticket-page__back {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: transparent;
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
    cursor: pointer;
    transition: color var(--transition-fast), border-color var(--transition-fast);
  }

  .ticket-page__back:hover {
    color: var(--color-text-primary);
    border-color: rgba(244, 241, 232, 0.18);
  }

  .ticket-page__flyer {
    width: 100%;
    aspect-ratio: 2 / 1;
    object-fit: cover;
    display: block;
    border: var(--border-subtle);
  }

  .ticket-page__event {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-5);
    padding: var(--space-6);
    border-radius: var(--radius-xl);
    flex-wrap: wrap;
  }

  .ticket-page__event-info h1 {
    margin: 0 0 var(--space-2);
    font-size: clamp(1.6rem, 4vw, 2.2rem);
    line-height: var(--line-height-tight);
  }

  .ticket-page__headline {
    margin: 0 0 var(--space-3);
    color: var(--color-text-secondary);
  }

  .ticket-page__eyebrow {
    margin: 0 0 var(--space-2);
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--color-text-amber);
  }

  .ticket-page__facts {
    margin: var(--space-3) 0 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .ticket-page__facts div {
    display: flex;
    gap: var(--space-3);
    font-size: var(--font-size-sm);
  }

  .ticket-page__facts dt {
    flex: 0 0 3.5rem;
    color: var(--color-text-muted);
  }

  .ticket-page__facts dd {
    margin: 0;
    color: var(--color-text-secondary);
  }

  .ticket-page__facts a {
    color: var(--color-text-amber);
  }

  .ticket-page__status {
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-pill);
    border: var(--border-ambient);
    color: var(--color-text-amber);
    font-size: var(--font-size-sm);
    font-weight: 600;
    white-space: nowrap;
  }

  .ticket-page__status--confirmed {
    border-color: rgba(123, 228, 149, 0.4);
    color: var(--color-success-500);
  }

  .ticket-page__about {
    padding: var(--space-5);
  }

  .ticket-page__cta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    padding: var(--space-5);
    border-radius: var(--radius-lg);
    flex-wrap: wrap;
  }

  .ticket-page__abandoned {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-3);
    padding: var(--space-6);
    border-radius: var(--radius-lg);
  }

  .ticket-page__abandoned h1 {
    margin: 0;
    font-size: clamp(1.4rem, 4vw, 1.9rem);
    line-height: var(--line-height-tight);
  }

  .ticket-page__abandoned p {
    margin: 0;
    color: var(--color-text-secondary);
  }

  .ticket-page__ladder {
    flex-basis: 100%;
    border-top: var(--border-subtle);
    padding-top: var(--space-3);
  }

  .ticket-page__cta p {
    margin: 0;
    color: var(--color-text-secondary);
  }

  /* The stub: a paper ticket on the ink page. */
  .ticket-page__stub {
    position: relative;
    display: flex;
    align-items: stretch;
    background: var(--color-text-primary);
    color: var(--color-text-inverse);
  }

  .ticket-page__stub-rail {
    flex: 0 0 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    border-left: 2px dashed rgba(10, 10, 11, 0.35);
    font-family: var(--font-mono);
    font-size: var(--font-size-2xs);
    letter-spacing: 0.34em;
    text-transform: uppercase;
    color: rgba(10, 10, 11, 0.55);
    padding: var(--space-3) 0;
  }

  /* Punch holes where the perforation meets the edges */
  .ticket-page__stub::before,
  .ticket-page__stub::after {
    content: '';
    position: absolute;
    left: calc(3rem - 8px);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--color-surface-950);
    z-index: 1;
  }

  .ticket-page__stub::before {
    top: -8px;
  }

  .ticket-page__stub::after {
    bottom: -8px;
  }

  .ticket-page__stub-body {
    flex: 1;
    padding: var(--space-5);
    text-align: center;
  }

  .ticket-page__stub-eyebrow {
    margin: 0 0 var(--space-2);
    font-family: var(--font-mono);
    font-size: var(--font-size-2xs);
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--color-accent-strong);
  }

  .ticket-page__code-value {
    margin: 0;
    /* Explicit ink color: the global `p { color: … }` selector targets this
       <p> directly and would otherwise wash it out against the paper stub. */
    color: var(--color-text-inverse);
    font-family: var(--font-mono);
    font-weight: 700;
    font-size: clamp(1.6rem, 5vw, 2.4rem);
    letter-spacing: 0.14em;
    overflow-wrap: anywhere;
  }

  .ticket-page__code-hint {
    margin: var(--space-2) 0 0;
    font-size: var(--font-size-xs);
    color: rgba(10, 10, 11, 0.6);
  }

  .ticket-page__extras {
    padding: var(--space-5);
    border-radius: var(--radius-lg);
  }

  .ticket-page__extras h3 {
    margin: 0 0 var(--space-3);
    font-size: var(--font-size-md);
  }

  .ticket-page__extras ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  .ticket-page__extras li {
    display: flex;
    justify-content: space-between;
    gap: var(--space-4);
    padding: var(--space-2) 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    border-bottom: var(--border-subtle);
    font-variant-numeric: tabular-nums;
  }

  .ticket-page__extras li:last-child {
    border-bottom: none;
  }

  .ticket-page__actions {
    display: flex;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .ticket-page__whatsapp {
    display: inline-flex;
    align-items: center;
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-pill);
    border: var(--border-strong);
    color: var(--color-text-primary);
    text-decoration: none;
    font-size: var(--font-size-sm);
    font-weight: 600;
    transition: border-color var(--transition-fast);
  }

  .ticket-page__whatsapp:hover {
    border-color: rgba(78, 224, 195, 0.5);
  }
</style>
